/*
 * Mural Studio hand-off into the EXISTING editors. Loaded on the custom-wallpaper product page
 * (<wallpaper-configurator>) and on the $65 sample kit page (<custom-sample-editor>).
 *
 * Inert unless the URL has ?studio=<token>: no network request, no DOM change, nothing.
 * With a token it:
 *   1. fetches the hand-off record from /apps/studio/api/handoff/<token> (410 = expired or studio off);
 *   2. waits for the editor to finish initialising;
 *   3. adds body.ac-studio-handoff (CSS below hides Rotate, Remove Image, the pattern-mode control and the
 *      kit's quality card) and shows a "Designing with AC Studio · Back to studio" banner;
 *   4. prefills the wall size in whole feet + inches;
 *   5. injects the watermarked JPEG into the editor's own #image-upload input via DataTransfer and fires
 *      `change`, exactly as if the shopper had chosen the file (fallback: editor.handleImageUpload);
 *      the file name "<Name> - AC Studio <CODE>.jpg" becomes the order's "Image Name";
 *   6. on the kit only, applies Step 3 ("Apply Wall Crop") once it has checked the frame covers the whole design,
 *      so the shopper lands on Step 4 (drag the 24" square); otherwise Step 3 is left to them;
 *   7. sends an ack beacon; on any failure it shows a notice with a link back and leaves the editor as is.
 * It never edits wallpaper-configurator.js / custom-sample-editor.js and never touches pricing or the cart.
 */
(function () {
  "use strict";

  var me = document.currentScript;   // read before anything async: it is only set while this file runs
  var token;
  try { token = new URLSearchParams(window.location.search).get("studio"); } catch (e) { return; }
  if (!token || !/^[A-Za-z0-9_-]{10,100}$/.test(token)) return;

  var API = "/apps/studio";
  var STUDIO_PAGE = (me && me.getAttribute("data-studio-page")) || "/pages/create-your-own-mural";
  var EDITORS = ["wallpaper-configurator", "custom-sample-editor"];

  function ack(ok, stage, error) {
    try {
      fetch(API + "/api/handoff/" + encodeURIComponent(token) + "/ack", {
        method: "POST", keepalive: true, credentials: "same-origin",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ ok: ok, stage: stage || null, error: error ? String(error).slice(0, 280) : null })
      }).catch(function () {});
    } catch (e) {}
  }

  function injectStyles() {
    if (document.getElementById("ac-studio-handoff-css")) return;
    var css = [
      "body.ac-studio-handoff #rotate-image,",
      "body.ac-studio-handoff #clear-image,",
      "body.ac-studio-handoff #pattern-settings,",
      "body.ac-studio-handoff #mode-pattern-btn,",
      "body.ac-studio-handoff custom-sample-editor .quality-card { display: none !important; }",
      ".ac-studio-banner { display: flex; flex-wrap: wrap; gap: 6px 12px; align-items: center; justify-content: center;",
      "  background: #e9f5f6; color: #375a5e; border: 1px solid #d6e3e4; border-radius: 8px; padding: 10px 14px;",
      "  margin: 12px auto; max-width: 1100px; font-size: 14px; line-height: 1.4; }",
      ".ac-studio-banner a { color: #375a5e; font-weight: 600; text-underline-offset: 3px; }",
      ".ac-studio-banner.is-error { background: #fff4ef; border-color: #f0c9b8; }"
    ].join("\n");
    var s = document.createElement("style");
    s.id = "ac-studio-handoff-css";
    s.textContent = css;
    document.head.appendChild(s);
  }

  function banner(editor, text, isError) {
    injectStyles();
    var b = document.querySelector(".ac-studio-banner");
    if (!b) {
      b = document.createElement("div");
      b.className = "ac-studio-banner";
      b.setAttribute("role", isError ? "alert" : "status");
      var anchor = editor || document.querySelector("main") || document.body;
      anchor.parentNode.insertBefore(b, anchor);
    }
    b.classList.toggle("is-error", !!isError);
    b.textContent = "";
    var span = document.createElement("span");
    span.textContent = text;
    var a = document.createElement("a");
    a.href = STUDIO_PAGE;
    a.textContent = "Back to studio";
    b.appendChild(span);
    b.appendChild(a);
  }

  function findEditor() {
    for (var i = 0; i < EDITORS.length; i++) {
      var node = document.querySelector(EDITORS[i]);
      if (node) return { node: node, tag: EDITORS[i] };
    }
    return null;
  }

  function waitFor(test, timeoutMs) {
    return new Promise(function (resolve, reject) {
      var start = Date.now();
      (function check() {
        var v;
        try { v = test(); } catch (e) { v = null; }
        if (v) return resolve(v);
        if (Date.now() - start > timeoutMs) return reject(new Error("timeout"));
        setTimeout(check, 100);
      })();
    });
  }

  function editorReady(ed) {
    return customElements.whenDefined(ed.tag).then(function () {
      return waitFor(function () {
        var n = ed.node;
        if (!n.elements || !n.state) return false;
        if (ed.tag === "wallpaper-configurator") return n.state.initialized === true;
        return true;   // custom-sample-editor sets everything up synchronously in connectedCallback
      }, 15000);
    });
  }

  function setField(scope, ids, value) {
    for (var i = 0; i < ids.length; i++) {
      var input = scope.querySelector("#" + ids[i]);
      if (input) {
        input.value = String(value);
        input.dispatchEvent(new Event("input", { bubbles: true }));
        input.dispatchEvent(new Event("change", { bubbles: true }));
        return true;
      }
    }
    return false;
  }

  function prefillWall(ed, wall) {
    if (!wall || !wall.width_in || !wall.height_in) return;
    var w = Math.round(wall.width_in), h = Math.round(wall.height_in);
    var n = ed.node;
    setField(n, ["width-feet", "input-width-feet"], Math.floor(w / 12));
    setField(n, ["width-inches", "input-width-inch"], w % 12);
    setField(n, ["height-feet", "input-height-feet"], Math.floor(h / 12));
    setField(n, ["height-inches", "input-height-inch"], h % 12);
  }

  function injectFile(ed, file) {
    var input = ed.node.querySelector("#image-upload");
    var viaDataTransfer = false;
    if (input && typeof DataTransfer === "function") {
      try {
        var dt = new DataTransfer();
        dt.items.add(file);
        input.files = dt.files;
        viaDataTransfer = input.files && input.files.length === 1;
      } catch (e) { viaDataTransfer = false; }
    }
    if (viaDataTransfer) {
      input.dispatchEvent(new Event("change", { bubbles: true }));
      return "datatransfer";
    }
    if (typeof ed.node.handleImageUpload === "function") {
      ed.node.handleImageUpload({ target: { files: [file], value: "" } });
      return "fallback";
    }
    throw new Error("no way to hand the file to the editor");
  }

  // Kit only (2026-09-28): the studio makes the design at the wall's own shape, so the kit editor's Step 3 frame
  // already covers the whole design. Apply it for the shopper, through the same applyWallCrop() their "Apply Wall
  // Crop" click runs, but only after checking the frame really is the whole design; otherwise Step 3 stays theirs.
  function applyKitWallCrop(ed) {
    if (ed.tag !== "custom-sample-editor") return Promise.resolve(false);
    return waitFor(function () {
      var n = ed.node, s = n.state;
      return s && s.cropper && s.cropper.ready && s.hasValidDimensions && !s.wallCropApplied &&
        typeof n.applyWallCrop === "function" ? n : null;
    }, 15000).then(function (n) {
      var c = n.state.cropper, img = c.getImageData(), d = c.getData(true);
      var whole = Math.abs(d.x) <= 2 && Math.abs(d.y) <= 2 &&
        Math.abs(d.width - img.naturalWidth) <= img.naturalWidth * 0.01 &&
        Math.abs(d.height - img.naturalHeight) <= img.naturalHeight * 0.01;
      if (!whole) return false;
      n.applyWallCrop();
      return n.state.wallCropApplied === true;
    }).catch(function () { return false; });
  }

  function run() {
    var ed = findEditor();
    var stage = "fetch";
    var record;
    fetch(API + "/api/handoff/" + encodeURIComponent(token), { credentials: "same-origin", cache: "no-store" })
      .then(function (r) {
        if (r.status === 410) { var e = new Error("expired"); e.expired = true; throw e; }
        if (!r.ok) throw new Error("handoff " + r.status);
        return r.json();
      })
      .then(function (rec) {
        record = rec;
        if (!ed) { stage = "find-editor"; throw new Error("editor not on this page"); }
        stage = "editor-ready";
        return editorReady(ed);
      })
      .then(function () {
        stage = "image";
        return fetch(record.image_url, { credentials: "same-origin" }).then(function (r) {
          if (!r.ok) throw new Error("image " + r.status);
          return r.blob();
        });
      })
      .then(function (blob) {
        stage = "inject";
        document.body.classList.add("ac-studio-handoff");
        injectStyles();
        banner(ed.node, "Designing with AC Studio: “" + record.name + "”.");
        prefillWall(ed, record.wall);
        var file = new File([blob], record.file_name, { type: "image/jpeg", lastModified: Date.now() });
        var how = injectFile(ed, file);
        stage = "verify";
        return waitFor(function () {
          var f = ed.node.state && ed.node.state.originalFile;
          return f && f.name === record.file_name ? how : null;
        }, 20000);
      })
      .then(function (how) {
        return applyKitWallCrop(ed).then(function (framed) {
          if (framed) {
            banner(ed.node, "Designing with AC Studio: “" + record.name + "”. It's framed to your wall: drag the 24\" × 24\" square to the part you want printed.");
          }
          ack(true, "loaded:" + how + (framed ? "+wallcrop" : ""));
        });
      })
      .catch(function (err) {
        if (err && err.expired) {
          banner(ed && ed.node, "This studio design link has expired. You can make a new one in the studio.", true);
          ack(false, "expired", "410");
          return;
        }
        if (stage === "verify") {
          // The file went in but the editor didn't confirm it: undo nothing, just say so.
          banner(ed && ed.node, "We couldn't confirm your design loaded. If you don't see it, go back to the studio and choose it again.", true);
        } else {
          document.body.classList.remove("ac-studio-handoff");
          banner(ed && ed.node, "We couldn't load your studio design here. Go back to the studio and choose it again, or upload an image as usual.", true);
        }
        ack(false, stage, err && err.message);
      });
  }

  if (document.readyState === "loading") document.addEventListener("DOMContentLoaded", run);
  else run();
})();
